import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Modal from '../../../components/Modal';
import { faEye, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import TableSkeleton from '../../../components/SkeletonLoader/TableSkeleton';
import {
  useGetPublicDocumentsMutation,
  useGetTeacherDocumentsMutation
} from '../../../features/authentication/assignmentDocumentApiSlice';
import PropTypes from 'prop-types';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import VideoModal from '../../assignments/components/questionDetailComponents/VideoModal';
import DocumentModal from '../../assignments/components/questionDetailComponents/DocumentModal';
import AudioModal from '../../assignments/components/questionDetailComponents/AudioModal';
import { useGetResourceDocMutation } from '../../../features/authentication/assignmentApislice';
import { ResourceUtils } from '../../assignments/components/questionDetailComponents/ResourceUtils';
import { useMyContentContext } from '../utils/myContentContext';
import CustomToast from '../../../components/Toast';
import { useParams } from 'react-router';
import { useAttachResourcesMutation } from '../../../features/authentication/qSetApiSlice';

const AddQuestionResource = ({
  isOpen,
  onClose
}) => {
  const [getPublicDocuments] = useGetPublicDocumentsMutation();
  const [getTeacherDocuments] = useGetTeacherDocumentsMutation();
  const [getResourceDoc] = useGetResourceDocMutation();
  const [attachResources] = useAttachResourcesMutation();

  const [documents, setDocuments] = useState([]);
  const [personalDocuments, setPersonalDocuments] = useState([]);
  const [publicDocuments, setPublicDocuments] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const [isDocsLoading, setIsDocsLoading] = useState(false);
  const [videoModal, setVideoModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [audioUrl, setAudioUrl] = useState('');
  const [documentText, setDocumentText] = useState('');

  const { setId, questionId, courseId } = useParams();

  const { selectedDocumentIds, setSelectedDocumentIds, questionDocuments, setQuestionDocuments } = useMyContentContext();

  useEffect(() => {
    handleTabSwitch(activeTab);
  }, []);

  const handleTabSwitch = (activeTab) => {
    setSearchText('');
    setDocuments([]);
    if (activeTab == 0)
      loadTeacherDocuments();
    else
      loadPublicDocuments();
  };

  const loadTeacherDocuments = async () => {
    setIsDocsLoading(true);
    const response = await getTeacherDocuments().unwrap();
    const modifiedResponse = ResourceUtils.addTypeToDocuments(response);
    setDocuments(modifiedResponse);
    setPersonalDocuments(modifiedResponse);
    setIsDocsLoading(false);
  };

  const loadPublicDocuments = async () => {
    setIsDocsLoading(true);
    const response = await getPublicDocuments().unwrap();
    const modifiedResponse = ResourceUtils.addTypeToDocuments(response);
    setDocuments(modifiedResponse);
    setPublicDocuments(modifiedResponse);
    setIsDocsLoading(false);
  };

  const handleSearch = (searchText) => {
    const docs = activeTab == 0 ? personalDocuments : publicDocuments;
    setSearchText(searchText);
    const filteredData = docs.filter(item =>
      item.description.toLowerCase().includes(searchText.toLowerCase())
    );
    setDocuments(filteredData);
  };

  const resourceViewHandler = async (resourceType, resourceId, resourceUrl) => {
    setDocumentText('');
    let link;
    switch (resourceType) {
    case 'Video':
      setVideoModal(true);
      setVideoUrl(resourceUrl);
      break;
    case 'Document':
      try {
        const option = { documentId: resourceId };
        setDocumentModal(true);
        const response = await getResourceDoc(option).unwrap();
        setDocumentText(response.resourceText);
      } catch (error) {
        console.error(error);
      }
      break;
    case 'Audio':
      setAudioModal(true);
      setAudioUrl(resourceUrl);
      break;
    case 'Link':
    case 'PDF':
    case 'MS Word':
    case 'Power Point':
      link = document.createElement('a');
      link.href = resourceUrl;
      link.target = '_blank';
      link.click();
      break;
    default:
      console.warn('Unknown resource type: ', resourceType);
    }
  };

  const handleDocumentSelection = (documentId, checked) => {
    if (documentId == -1) {
      if (checked) {
        const allDocIds = documents.map(obj => obj.documentId);
        setSelectedDocumentIds(allDocIds);
      }
      else
        setSelectedDocumentIds([]);
    }
    else {
      if (checked)
        setSelectedDocumentIds((prevData) => {
          return [...prevData, documentId];
        });
      else {
        const newIds = selectedDocumentIds.filter((id) => id != documentId);
        setSelectedDocumentIds(newIds);
      }
    }
  };

  const handleAttachDocument = () => {
    if (selectedDocumentIds.length > 0) {
      const selectedQuestPrivateDocs = personalDocuments.filter((item) => selectedDocumentIds.includes(item.documentId));
      const selectedQuestPublicDocs = publicDocuments.filter((item) => selectedDocumentIds.includes(item.documentId));
      const docs = [...selectedQuestPrivateDocs, ...selectedQuestPublicDocs.filter(item2 => !selectedQuestPrivateDocs.some(item1 => item1.documentId === item2.documentId))];
      const updatedDocs = questionDocuments.filter((item) => selectedDocumentIds.includes(item.documentId));
      const newDocs = [...updatedDocs, ...docs.filter(item2 => !updatedDocs.some(item1 => item1.documentId === item2.documentId))];
      const positionedDocs = newDocs.map((item, index) => { return { ...item, position: index + 1 }; });
      setQuestionDocuments(positionedDocs);
      if (questionId) {
        attachResourcesAsync(positionedDocs.map((obj) => { return { id: obj.documentId, displayWithQuestion: false }; }));
      }
      onClose();
    }
    else
      CustomToast('You must select atleast one document before attaching.', 'error');
  };

  const attachResourcesAsync = async (documents) => {
    const option = {
      questionId: questionId,
      setId: setId,
      courseId: courseId,
      documents: documents
    };
    const response = await attachResources(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      CustomToast('Resources added successfully', 'success');
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        titleName='Attach Documents'
        contentWidth='1000px'
        detailHeight='400px'>
        <div className="d-flex flex-column pb-4 primary-tab">
          <div className="d-inline-flex btn-group w-100 mb-4">
            <div className={`btn ${activeTab == 0 ? 'active' : ''} p-2 w-100 justify-content-center`} onClick={() => { handleTabSwitch(0); setIsDocsLoading(true); setActiveTab(0); }}>
              Personal Documents
            </div>
            <div className={`btn ${activeTab == 1 ? 'active' : ''} p-2 w-100 justify-content-center`} onClick={() => { handleTabSwitch(1); setIsDocsLoading(true); setActiveTab(1); }}>
              Public Documents
            </div>
          </div>
          <p>Select one or more documents and click <strong>Attach</strong> to attach document(s) to question.<br /> Note that the documents attached immediately to the question without having to save changes.</p>
          <Row id='searchBox' className="col-12 m-0 mb-3">
            <Col className="custom-search position-relative p-0">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder='Find documents...'
                className="form-control px-5"
                id="searchDocuments"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {(searchText && <FontAwesomeIcon
                icon={faXmark}
                className='fs-5 start-100 cursor-pointer ml-n25'
                onClick={() => handleSearch('')} />
              )}
            </Col>
          </Row>
          {!isDocsLoading ?
            (
              <div className='h-320'>
                {documents.length > 0 ? (
                  <>
                    <div className="custom-table table-responsive mt-3 h-320">
                      <table className='table reportTable'>
                        <thead>
                          <tr>
                            <th>
                              <input
                                type='checkbox'
                                checked={selectedDocumentIds.length == documents.length}
                                onChange={(e) => handleDocumentSelection(-1, e.target.checked)}
                              />
                            </th>
                            <th id='documentName'>Document Name</th>
                            <th>{' '}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {documents.map((item) => {
                            return (
                              <>
                                <tr>
                                  <td>
                                    <input
                                      type='checkbox'
                                      checked={(selectedDocumentIds.filter((id) => id == item.documentId)).length > 0}
                                      onChange={(e) => handleDocumentSelection(item.documentId, e.target.checked)}
                                    />
                                  </td>
                                  <td>{item.description}</td>
                                  <td><OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip className="custom-tooltip">View</Tooltip>
                                    }>
                                    <button
                                      className="btn grid-icon-btn primary-icon-btn"
                                      onClick={() => resourceViewHandler(item.type, item.documentId, item.url)}>
                                      <FontAwesomeIcon icon={faEye} className='fs-6' />
                                    </button>
                                  </OverlayTrigger></td>
                                </tr>
                              </>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className='d-flex gap-2 justify-content-end mt-3'>
                      <button className='btn btn-castleWhite w-10' onClick={onClose}>Cancel</button>
                      <button className='btn btn-castleRed w-10' onClick={() => handleAttachDocument()}>Attach</button>
                    </div>
                  </>
                ) : (
                  <div className='text-danger text-center mt-4'>There is no documents found.</div>
                )}
              </div>
            ) : (
              <TableSkeleton
                rowCount={8}
                colCount={3}
              />
            )}
        </div>
      </Modal>
      <VideoModal videoUrl={videoUrl} isOpen={videoModal} onClose={() => setVideoModal(false)} />
      <DocumentModal value={documentText} isOpen={documentModal} onClose={() => setDocumentModal(false)} />
      <AudioModal audioUrl={audioUrl} isOpen={audioModal} onClose={() => setAudioModal(false)} />
    </>
  );
};

AddQuestionResource.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default AddQuestionResource;